// apps/client/src/components/common/SkeletonLoader.tsx
// Skeleton loading components for progressive UI rendering
// Uses shimmer animation for better perceived performance (20-30% faster than pulse)

import React from 'react';

/**
 * Base Skeleton Component
 * Animated gray placeholder with shimmer effect
 */
interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '', width, height }) => {
  const style: React.CSSProperties = {};
  if (width) style.width = width;
  if (height) style.height = height;

  return (
    <div
      className={`relative bg-gray-700 rounded overflow-hidden ${className}`}
      style={style}
    >
      {/* Shimmer overlay */}
      <div className="absolute inset-0 animate-shimmer" />
    </div>
  );
};

/**
 * Skeleton for StatCard
 * Matches StatCard component dimensions
 */
export const SkeletonStatCard: React.FC = () => {
  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-md">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <Skeleton width="60%" height="20px" className="mb-2" />
          <Skeleton width="40%" height="14px" />
        </div>
        <Skeleton width="40px" height="40px" className="rounded-full" />
      </div>
      <Skeleton width="80%" height="32px" />
    </div>
  );
};

/**
 * Skeleton for Chart
 * Matches MonthlyBreakdownChart component
 */
export const SkeletonChart: React.FC = () => {
  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-md">
      <Skeleton width="40%" height="24px" className="mb-6" />
      <div className="flex items-end justify-between h-64 gap-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1"
            height={`${Math.random() * 60 + 40}%`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} width="20px" height="12px" />
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton for Dashboard Filters
 */
export const SkeletonFilters: React.FC = () => {
  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-md flex flex-wrap items-end gap-4">
      <div className="flex-1 min-w-[200px]">
        <Skeleton width="80px" height="16px" className="mb-2" />
        <Skeleton width="100%" height="42px" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <Skeleton width="80px" height="16px" className="mb-2" />
        <Skeleton width="100%" height="42px" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <Skeleton width="80px" height="16px" className="mb-2" />
        <Skeleton width="100%" height="42px" />
      </div>
    </div>
  );
};

/**
 * Skeleton for Template Tabs
 */
export const SkeletonTemplateTabs: React.FC = () => {
  return (
    <div className="border-b border-gray-700">
      <nav className="flex space-x-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} width="120px" height="20px" className="mb-3" />
        ))}
      </nav>
    </div>
  );
};

/**
 * Skeleton for Page Header with gradient underline
 */
export const SkeletonPageHeader: React.FC<{ showSubtitle?: boolean }> = ({
  showSubtitle = true,
}) => {
  return (
    <div>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Skeleton width="300px" height="32px" />
        </div>
        <Skeleton width="40px" height="40px" className="rounded-lg" />
      </div>
      <div className="gradient-underline mt-3" />
      {showSubtitle && (
        <Skeleton width="200px" height="16px" className="mt-2" />
      )}
    </div>
  );
};

/**
 * Complete Dashboard Skeleton
 * Shows full loading state for dashboard page.
 * Grid shape is configurable so the skeleton matches the target page's final
 * layout exactly (zero layout shift): main dashboard = 6 stat cards in 3
 * columns + 6 charts in 2 columns; office dashboard keeps the 4/4/1 defaults.
 * No page-level padding — AppLayout's <main> provides it.
 */
export const SkeletonDashboard: React.FC<{
  showTemplateTabs?: boolean;
  statCardCount?: number;
  statColumns?: 3 | 4;
  chartCount?: number;
  chartColumns?: 1 | 2;
}> = ({
  showTemplateTabs = false,
  statCardCount = 4,
  statColumns = 4,
  chartCount = 4,
  chartColumns = 1,
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <SkeletonPageHeader />

      {/* Template Tabs */}
      {showTemplateTabs && <SkeletonTemplateTabs />}

      {/* Filters */}
      <SkeletonFilters />

      {/* Stat Cards */}
      <SkeletonStatCards count={statCardCount} columns={statColumns} />

      {/* Charts */}
      <SkeletonCharts count={chartCount} columns={chartColumns} />
    </div>
  );
};

/**
 * Partial loading skeletons for progressive rendering
 */
// Literal class maps — Tailwind JIT purges interpolated class names
const STAT_COLUMN_CLASSES: Record<3 | 4, string> = {
  3: 'grid grid-cols-1 md:grid-cols-3 gap-6',
  4: 'grid grid-cols-1 md:grid-cols-4 gap-6',
};

const CHART_COLUMN_CLASSES: Record<1 | 2, string> = {
  1: 'grid grid-cols-1 gap-6',
  2: 'grid grid-cols-1 lg:grid-cols-2 gap-6',
};

export const SkeletonStatCards: React.FC<{ count?: number; columns?: 3 | 4 }> = ({
  count = 4,
  columns = 4,
}) => {
  return (
    <div className={STAT_COLUMN_CLASSES[columns]}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonStatCard key={i} />
      ))}
    </div>
  );
};

export const SkeletonCharts: React.FC<{ count?: number; columns?: 1 | 2 }> = ({
  count = 4,
  columns = 1,
}) => {
  return (
    <div className={CHART_COLUMN_CLASSES[columns]}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonChart key={i} />
      ))}
    </div>
  );
};

/**
 * Full-page skeleton for report pages (pages/reports/*): header + filters +
 * optional stat cards + table. Swap in for `if (loading) return <spinner>`
 * with no other structural change.
 */
export const SkeletonReportPage: React.FC<{
  statCards?: number;
  statColumns?: 3 | 4;
  tableRows?: number;
  tableColumns?: number;
}> = ({ statCards = 0, statColumns = 4, tableRows = 8, tableColumns = 5 }) => {
  return (
    <div className="space-y-6">
      <SkeletonPageHeader />
      <SkeletonFilters />
      {statCards > 0 && <SkeletonStatCards count={statCards} columns={statColumns} />}
      <SkeletonTable rows={tableRows} columns={tableColumns} />
    </div>
  );
};

/**
 * Full-page skeleton for list/table pages: header + toolbar row + table.
 */
export const SkeletonTablePage: React.FC<{
  tableRows?: number;
  tableColumns?: number;
}> = ({ tableRows = 8, tableColumns = 5 }) => {
  return (
    <div className="space-y-6">
      <SkeletonPageHeader />
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton width="240px" height="40px" />
        <Skeleton width="140px" height="40px" />
        <Skeleton width="140px" height="40px" />
      </div>
      <SkeletonTable rows={tableRows} columns={tableColumns} />
    </div>
  );
};

/**
 * Skeleton for Table rows
 */
export const SkeletonTableRow: React.FC<{ columns?: number }> = ({ columns = 5 }) => {
  return (
    <tr className="border-b border-gray-700">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton width={i === 0 ? '100%' : '80%'} height="20px" />
        </td>
      ))}
    </tr>
  );
};

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 5,
}) => {
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-700">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-4 py-3 text-left">
                <Skeleton width="80px" height="16px" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
};
